<template>
  <div class="volunteer-summary">
    <!-- Header: name + circular progress -->
    <div class="summary-header">
      <div class="progress-ring-wrap">
        <svg class="progress-ring" width="72" height="72" viewBox="0 0 72 72">
          <circle class="ring-bg" cx="36" cy="36" r="30" />
          <circle
            class="ring-fill"
            :class="progressColorClass"
            cx="36" cy="36" r="30"
            :style="{ strokeDashoffset: ringOffset }"
          />
        </svg>
        <span class="progress-pct" :class="progressColorClass">{{ percentage }}%</span>
      </div>
      <div class="header-text">
        <h6 class="header-title mb-0">{{ personLabel }}</h6>
        <span class="header-subtitle">{{ completenessSubtitle }}</span>
      </div>
    </div>

    <!-- Summary rows -->
    <div class="summary-rows">
      <div
        v-for="row in summaryRows"
        :key="row.key"
        class="summary-row"
      >
        <div class="row-main" @click="toggle(row.key)">
          <div class="row-icon-wrap" :class="`bg-${row.iconColor}`">
            <i class="material-icons">{{ row.icon }}</i>
          </div>
          <div class="row-body">
            <div class="row-heading">
              <span class="row-label">{{ row.label }}</span>
              <span class="status-badge" :class="`status-${row.statusColor}`">{{ row.status }}</span>
            </div>
            <p class="row-description">{{ row.description }}</p>
          </div>
          <i class="material-icons row-chevron" :class="{ expanded: expanded[row.key] }">expand_more</i>
        </div>

        <!-- Expanded detail items -->
        <transition name="slide">
          <div v-if="expanded[row.key] && row.items.length" class="row-detail">
            <ul class="detail-list">
              <li v-for="(item, idx) in row.items" :key="idx" class="detail-item">
                {{ formatItem(item) }}
              </li>
            </ul>
          </div>
        </transition>
        <transition name="slide">
          <div v-if="expanded[row.key] && !row.items.length" class="row-detail">
            <p class="detail-empty">No items to display.</p>
          </div>
        </transition>
      </div>
    </div>

    <!-- Action bar -->
    <div v-if="hasActions" class="action-bar">
      <button v-if="actionUris.userPreferences" class="btn btn-sm btn-outline-primary action-btn" @click="$emit('navigate', actionUris.userPreferences)">
        <i class="material-icons">favorite</i> Update Preferences
      </button>
      <button v-if="actionUris.userAvailability" class="btn btn-sm btn-outline-info action-btn" @click="$emit('navigate', actionUris.userAvailability)">
        <i class="material-icons">calendar_today</i> Set Availability
      </button>
      <button v-if="actionUris.signUpAssignments" class="btn btn-sm btn-outline-success action-btn" @click="$emit('navigate', actionUris.signUpAssignments)">
        <i class="material-icons">search</i> View Sign-up Opportunities
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue';

const props = defineProps({
  summary: { type: Object, default: () => ({}) },
  personName: { type: String, default: '' },
  /** URIs keyed by module id: { userPreferences, userAvailability, signUpAssignments } */
  actionUris: { type: Object, default: () => ({}) },
});

defineEmits(['navigate']);

/* ── Progress ring ── */
const CIRCUMFERENCE = 2 * Math.PI * 30; // r=30

// Use pre-computed setup_completeness from the API
const setupCompleteness = computed(() => props.summary?.setup_completeness || {});

const configuredCount = computed(() =>
  setupCompleteness.value.configured_sections?.length || 0
);
const totalRequiredCount = computed(() =>
  configuredCount.value + (setupCompleteness.value.missing_sections?.length || 0)
);

const percentage = computed(() => setupCompleteness.value.percentage || 0);
const ringOffset = computed(() => CIRCUMFERENCE - (CIRCUMFERENCE * percentage.value) / 100);

const progressColorClass = computed(() => {
  const pct = percentage.value;
  if (pct >= 75) return 'clr-green';
  if (pct >= 25) return 'clr-amber';
  return 'clr-red';
});

const personLabel = computed(() => props.personName || 'Volunteer Summary');
const completenessSubtitle = computed(() =>
  `${configuredCount.value} of ${totalRequiredCount.value} key sections configured`
);

/* ── Section icon / color mapping ── */
const SECTION_META = {
  skills:                 { icon: 'star',            iconColor: 'amber'  },
  interest_skills:        { icon: 'auto_awesome',    iconColor: 'purple' },
  activity_preferences:   { icon: 'favorite',        iconColor: 'rose'   },
  workgroup_preferences:  { icon: 'group_work',      iconColor: 'rose'   },
  availability:           { icon: 'event',           iconColor: 'teal'   },
  scheduling_rules:       { icon: 'rule',            iconColor: 'indigo' },
  signup_opportunities:   { icon: 'search',          iconColor: 'blue'   },
};

const STATUS_COLORS = {
  configured: 'green',
  open:       'blue',
  empty:      'grey',
  available:  'teal',
  none:       'grey',
};

/* ── Format an item for display based on its shape ── */
function formatItem(item) {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return String(item);
  // Skills (assigned and interest)
  if (item.skillname) return item.skillname;
  // Activities
  if (item.activityname) return item.activityname;
  // Workgroups
  if (item.workgroupname) return item.workgroupname;
  if (item.a_workgroup_name) return item.a_workgroup_name;
  // Generic fallbacks
  return item.name || item.label || item.description || item.title || JSON.stringify(item);
}

/* ── Map summary_descriptions section names to top-level data keys ── */
const SECTION_DATA_MAP = {
  activity_preferences:  () => props.summary?.preferences?.preferred_activities?.items || [],
  workgroup_preferences: () => props.summary?.preferences?.preferred_workgroups?.items || [],
};

/* ── Extract expandable items from the top-level key ── */
function extractItems(section) {
  // Use explicit mapping for sections whose names differ from top-level keys
  if (SECTION_DATA_MAP[section]) return SECTION_DATA_MAP[section]();

  const data = props.summary?.[section];
  if (!data) return [];

  // Direct array
  if (Array.isArray(data)) return data;

  // Object with .items
  if (Array.isArray(data.items)) return data.items;

  return [];
}

/* ── Build summary rows from summary_descriptions ── */
const summaryRows = computed(() => {
  const descriptions = props.summary?.summary_descriptions || [];
  return descriptions.map(d => {
    const meta = SECTION_META[d.section] || { icon: 'info', iconColor: 'grey' };
    return {
      key: d.section,
      label: d.label,
      status: d.status || 'none',
      statusColor: STATUS_COLORS[d.status] || 'grey',
      description: d.description || '',
      icon: meta.icon,
      iconColor: meta.iconColor,
      items: extractItems(d.section),
    };
  });
});

/* ── Expand / collapse state ── */
const expanded = reactive({});
function toggle(key) {
  expanded[key] = !expanded[key];
}

/* ── Action bar visibility ── */
const hasActions = computed(() =>
  props.actionUris.userPreferences || props.actionUris.userAvailability || props.actionUris.signUpAssignments
);
</script>

<style scoped>
.volunteer-summary {
  padding: 0.5rem 0;
}

/* ── Header ── */
.summary-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0 1.25rem;
  border-bottom: 1px solid #eee;
  margin-bottom: 0.75rem;
}

.progress-ring-wrap {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}
.progress-ring {
  transform: rotate(-90deg);
}
.ring-bg {
  fill: none;
  stroke: #e9ecef;
  stroke-width: 6;
}
.ring-fill {
  fill: none;
  stroke-width: 6;
  stroke-linecap: round;
  stroke-dasharray: 188.5; /* 2πr */
  transition: stroke-dashoffset 0.6s ease;
}
.ring-fill.clr-green { stroke: #4caf50; }
.ring-fill.clr-amber { stroke: #ff9800; }
.ring-fill.clr-red   { stroke: #f44336; }

.progress-pct {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
}
.progress-pct.clr-green { color: #4caf50; }
.progress-pct.clr-amber { color: #ff9800; }
.progress-pct.clr-red   { color: #f44336; }

.header-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #344767;
}
.header-subtitle {
  font-size: 0.78rem;
  color: #8392ab;
}

/* ── Summary rows ── */
.summary-rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-row {
  border-radius: 10px;
  overflow: hidden;
}
.row-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s;
}
.row-main:hover {
  background: #f8f9fa;
}

.row-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.row-icon-wrap i { font-size: 20px; }

/* reuse the bg-* classes from the app or define local */
.row-icon-wrap.bg-amber  { background: linear-gradient(135deg, #ffb300, #ff8f00); }
.row-icon-wrap.bg-purple { background: linear-gradient(135deg, #9c27b0, #7b1fa2); }
.row-icon-wrap.bg-rose   { background: linear-gradient(135deg, #e91e63, #c2185b); }
.row-icon-wrap.bg-teal   { background: linear-gradient(135deg, #009688, #00796b); }
.row-icon-wrap.bg-indigo { background: linear-gradient(135deg, #667eea, #764ba2); }
.row-icon-wrap.bg-blue   { background: linear-gradient(135deg, #2196f3, #1976d2); }
.row-icon-wrap.bg-grey   { background: #bbb; }

.row-body {
  flex: 1;
  min-width: 0;
}
.row-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.row-label {
  font-weight: 700;
  font-size: 0.88rem;
  color: #344767;
}

/* ── Status badge ── */
.status-badge {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.status-green { background: #e8f5e9; color: #2e7d32; }
.status-blue  { background: #e3f2fd; color: #1565c0; }
.status-grey  { background: #f5f5f5; color: #9e9e9e; }
.status-teal  { background: #e0f2f1; color: #00695c; }

.row-description {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #6c757d;
  line-height: 1.4;
}

.row-chevron {
  color: #adb5bd;
  transition: transform 0.25s ease;
  margin-top: 6px;
  flex-shrink: 0;
}
.row-chevron.expanded {
  transform: rotate(180deg);
}

/* ── Expanded detail ── */
.row-detail {
  padding: 0 0.5rem 0.75rem 3.25rem;
}
.detail-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.detail-item {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 0.76rem;
  font-weight: 600;
  background: #f0f0f0;
  color: #555;
}
.detail-empty {
  font-size: 0.78rem;
  color: #adb5bd;
  font-style: italic;
  margin: 0;
}

/* ── Slide transition ── */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.slide-enter-to,
.slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

/* ── Action bar ── */
.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 0 0;
  margin-top: 0.75rem;
  border-top: 1px solid #eee;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
}
.action-btn i {
  font-size: 16px;
}
</style>
